<template>
  <div class="question-builder">
    <div class="questions-list">
      <div v-for="(q, idx) in questions" :key="q.uid" class="question-row">
        <div class="question-order">{{ idx + 1 }}</div>
        <div class="question-info">
          <div class="question-label">{{ q.label || "Sem título" }}</div>
          <div class="question-meta">
            <span class="q-type-badge">{{ typeLabel(q.field_type) }}</span>
            <span v-if="q.required" class="q-req-badge">Obrigatória</span>
            <span
              v-if="q.field_type === 'select' && (q.options || []).length"
              class="q-options-hint"
            >
              {{ q.options.join(", ") }}
            </span>
          </div>
        </div>
        <div class="question-actions">
          <button
            class="mini-btn"
            title="Mover para cima"
            :disabled="idx === 0"
            @click="moveQuestion(idx, -1)"
          >
            <i class="fa fa-arrow-up"></i>
          </button>
          <button
            class="mini-btn"
            title="Mover para baixo"
            :disabled="idx === questions.length - 1"
            @click="moveQuestion(idx, 1)"
          >
            <i class="fa fa-arrow-down"></i>
          </button>
          <button class="mini-btn" title="Editar" @click="editQuestion(idx)">
            <i class="fa fa-pencil"></i>
          </button>
          <button
            class="mini-btn btn-del"
            title="Remover"
            @click="removeQuestion(idx)"
          >
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </div>

      <div v-if="!questions.length" class="empty-hint">
        Nenhuma pergunta adicionada ainda.
      </div>
    </div>

    <button class="btn btn-outline-primary btn-sm mt-2" @click="openAddModal">
      <i class="fa fa-plus"></i> Adicionar pergunta
    </button>

    <!-- Modal: adicionar/editar pergunta -->
    <div v-if="showModal" class="modal-mask" @click.self="closeModal">
      <div class="modal-card">
        <div class="modal-head">
          <div class="modal-title">
            {{ editingIndex === null ? "Adicionar pergunta" : "Editar pergunta" }}
          </div>
          <button class="modal-x" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <label class="lbl">Pergunta <span class="req">*</span></label>
          <input
            v-model="draft.label"
            class="form-control"
            placeholder="Ex: A entrega foi validada?"
            @keyup.enter="confirmQuestion"
          />

          <div class="row mt-2">
            <div class="col-md-12">
              <label class="lbl">Tipo de resposta</label>
              <select v-model="draft.field_type" class="form-control">
                <option v-for="t in fieldTypes" :key="t.value" :value="t.value">
                  {{ t.label }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="draft.field_type === 'select'" class="mt-2">
            <label class="lbl">Opções</label>
            <div
              v-for="(opt, oi) in draft.options"
              :key="oi"
              class="d-flex mb-1"
              style="gap: 6px"
            >
              <input
                v-model="draft.options[oi]"
                class="form-control"
                placeholder="Opção"
              />
              <button
                class="btn btn-sm btn-outline-danger"
                type="button"
                @click="draft.options.splice(oi, 1)"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
            <button
              class="btn btn-sm btn-outline-secondary mt-1"
              type="button"
              @click="draft.options.push('')"
            >
              <i class="fa fa-plus"></i> Opção
            </button>
          </div>

          <div class="row mt-3">
            <div class="col-md-12 d-flex align-items-center" style="gap: 10px">
              <input
                id="qRequired"
                type="checkbox"
                v-model="draft.required"
              />
              <label for="qRequired" class="m-0 lbl" style="cursor: pointer">
                Resposta obrigatória
              </label>
            </div>
          </div>

          <div v-if="draftError" class="alert alert-danger mt-2 p-2">
            {{ draftError }}
          </div>
        </div>
        <div class="modal-foot">
          <button class="btn btn-outline-secondary" @click="closeModal">
            Cancelar
          </button>
          <button class="btn btn-primary" @click="confirmQuestion">
            {{ editingIndex === null ? "Adicionar" : "Salvar" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "QuestionBuilder",

  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      questions: this.cloneQuestions(this.value),
      showModal: false,
      editingIndex: null,
      draftError: "",
      draft: this.emptyDraft(),
      fieldTypes: [
        { value: "text", label: "Texto curto" },
        { value: "boolean", label: "Sim / Não" },
        { value: "select", label: "Múltipla escolha" },
        { value: "file", label: "Anexo de arquivo" },
      ],
    };
  },

  watch: {
    value(newVal) {
      this.questions = this.cloneQuestions(newVal);
    },
  },

  methods: {
    cloneQuestions(list) {
      return (list || []).map((q, i) => ({
        uid: q.uid || `q_${Date.now()}_${i}_${Math.random().toString(16).slice(2)}`,
        label: q.label || "",
        field_type: q.field_type || "text",
        options: Array.isArray(q.options) ? [...q.options] : [],
        required: q.required !== undefined ? !!q.required : true,
      }));
    },

    emptyDraft() {
      return { label: "", field_type: "text", options: [], required: true };
    },

    typeLabel(value) {
      const found = this.fieldTypes.find((t) => t.value === value);
      return found ? found.label : value;
    },

    emitChange() {
      this.$emit("input", this.questions);
    },

    openAddModal() {
      this.editingIndex = null;
      this.draft = this.emptyDraft();
      this.draftError = "";
      this.showModal = true;
    },

    editQuestion(idx) {
      this.editingIndex = idx;
      const q = this.questions[idx];
      this.draft = { ...q, options: [...(q.options || [])] };
      this.draftError = "";
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editingIndex = null;
      this.draftError = "";
    },

    confirmQuestion() {
      const label = (this.draft.label || "").trim();
      if (!label) {
        this.draftError = "Informe o texto da pergunta.";
        return;
      }

      const question = {
        uid:
          this.editingIndex !== null
            ? this.questions[this.editingIndex].uid
            : `q_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        label,
        field_type: this.draft.field_type,
        options:
          this.draft.field_type === "select"
            ? (this.draft.options || []).map((o) => o.trim()).filter(Boolean)
            : [],
        required: !!this.draft.required,
      };

      if (this.editingIndex === null) {
        this.questions.push(question);
      } else {
        this.$set(this.questions, this.editingIndex, question);
      }

      this.emitChange();
      this.closeModal();
    },

    removeQuestion(idx) {
      this.questions.splice(idx, 1);
      this.emitChange();
    },

    moveQuestion(idx, dir) {
      const to = idx + dir;
      if (to < 0 || to >= this.questions.length) return;
      const copy = [...this.questions];
      [copy[idx], copy[to]] = [copy[to], copy[idx]];
      this.questions = copy;
      this.emitChange();
    },
  },
};
</script>

<style scoped>
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.question-row {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
}
.question-order {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #9ca3af;
  color: #fff;
  font-weight: 900;
  font-size: 12px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.question-info {
  flex: 1;
  min-width: 0;
}
.question-label {
  font-weight: 800;
  color: #111827;
}
.question-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
}
.q-type-badge {
  font-size: 11px;
  font-weight: 700;
  color: #2563eb;
  background: #eff6ff;
  border-radius: 999px;
  padding: 2px 8px;
}
.q-req-badge {
  font-size: 11px;
  font-weight: 700;
  color: #b91c1c;
  background: #fef2f2;
  border-radius: 999px;
  padding: 2px 8px;
}
.q-options-hint {
  font-size: 11px;
  color: #6b7280;
}
.question-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.mini-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}
.mini-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.btn-del {
  color: #dc2626;
}
.empty-hint {
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
  padding: 10px;
}
.lbl {
  font-weight: 800;
  color: #374151;
  margin-bottom: 6px;
  display: block;
}
.req {
  color: #dc2626;
}

/* Modal */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 18px;
}
.modal-card {
  width: 520px;
  max-width: 95vw;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.18);
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
}
.modal-title {
  font-weight: 900;
  color: #111827;
}
.modal-x {
  border: 0;
  background: transparent;
  font-size: 22px;
  font-weight: 900;
  color: #6b7280;
  cursor: pointer;
}
.modal-body {
  padding: 14px 16px;
}
.modal-foot {
  padding: 14px 16px;
  border-top: 1px solid #eef2f7;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
